import type { Loader } from '@googlemaps/js-api-loader'
import type { AxiosResponse } from 'axios'
import type { ReactElement } from 'react'

import { Status, Wrapper } from '@googlemaps/react-wrapper'
import axios from 'axios'
import PropTypes from 'prop-types'
import { useEffect, useState, useContext } from 'react'
import styled from 'styled-components'

import { ListingIdContext } from 'context/listingIdContext'
import env from 'env'
import { useTracker } from 'store/@hooks/use-tracker'
import { LISTING_PUBLIC_PAGE } from 'tracker/events'
import GoogleDistanceMatrixAdaptor from 'utils/google-distance-matrix-adaptor/google-distance-matrix-adaptor'

import { GOOGLE_RESPONSE_STATUS } from './constants'
import { Map } from './map'
import { StreetView } from './street-view'
import { StyledBackButton, StyledImageButton, StyledStreetViewSpan } from './styled'

const LoadingMap = styled.div`
  width: 100%;
  height: 100%;
  background-color: #f5f5f5;
  color: #aaa;
  display: flex;
  justify-content: center;
  align-items: center;
`

const renderFunc = (status: Status): ReactElement => {
  if (status === Status.SUCCESS) return <></>
  return (
    <LoadingMap>
      <h3>Google Map is {status}</h3>
    </LoadingMap>
  )
}

const generateStreetViewUrl = (center: google.maps.LatLngLiteral) =>
  `https://maps.googleapis.com/maps/api/streetview?size=300x200&location=${center.lat},${center.lng}&key=${env.GOOGLE_MAP_KEY}`

export type GoogleServices = {
  map: google.maps.Map
  places?: google.maps.places.PlacesService
  distanceMatrixService: IDistanceService
}

interface ListingMapProps extends google.maps.MapOptions {
  render?: (status: Status) => ReactElement
  style?: Record<string, string>
  callback?: (status: Status, googleMap: typeof google.maps | null) => void
  onClick?: (e: google.maps.MapMouseEvent) => void
  onZoom?: () => void
  onDragStart?: () => void
  onMapRender?: (services: GoogleServices) => void
  onFullScreen?: () => void
  onExitFullScreen?: () => void
  onStreetViewFullScreen?: () => void
}

export const GoogleMap: React.FC<ListingMapProps> = ({
  render,
  style,
  callback,
  children,
  onClick,
  onMapRender,
  onFullScreen,
  onExitFullScreen,
  onStreetViewFullScreen,
  ...options
}) => {
  const listingId = useContext(ListingIdContext)
  const track = useTracker()
  const [googleMap, setGoogleMap] = useState<typeof google.maps>()
  const [isMapHidden, setIsMapHidden] = useState(false)
  const [isShowStreetView, setIsShowStreetView] = useState(false)
  const center = options.center as google.maps.LatLngLiteral

  const handleStreetViewMetaData = async (location: google.maps.LatLngLiteral) => {
    const url = `https://maps.googleapis.com/maps/api/streetview/metadata?location=${location.lat},${location.lng}&key=${env.GOOGLE_MAP_KEY}`
    const response = await axios.get<GoogleMapAPIResponse, AxiosResponse<GoogleMapAPIResponse>>(url)
    if (response?.data?.status === GOOGLE_RESPONSE_STATUS.OK) {
      setIsShowStreetView(true)
    }
  }

  useEffect(() => {
    if (center) {
      handleStreetViewMetaData(center).catch(console.error)
    }
  }, [center])

  const handleIsRender = () => {
    if (!isMapHidden) {
      track(LISTING_PUBLIC_PAGE.CLICK_STREET_VIEW, {
        listing_id: listingId,
      })
    }
    setIsMapHidden(!isMapHidden)
  }

  const handleCallback = async (status, loader: Loader) => {
    callback && callback(status, null) // eslint-disable-line
    if (status !== Status.SUCCESS) return

    const { maps } = await loader.load()
    callback && callback(status, maps) // eslint-disable-line
    setGoogleMap(google.maps)
  }

  const handleOnLoad = (map: google.maps.Map) => {
    if (!googleMap) throw new Error('Google Map is not loaded')

    const places = new googleMap.places.PlacesService(map) // eslint-disable-line
    const googleDistanceMatrixService = new googleMap.DistanceMatrixService()
    const distanceMatrixService = new GoogleDistanceMatrixAdaptor(googleDistanceMatrixService)
    // eslint-disable-next-line
    onMapRender &&
      onMapRender({
        map,
        places,
        distanceMatrixService,
      })
  }

  return (
    <Wrapper callback={handleCallback} apiKey={env.GOOGLE_MAP_KEY} libraries={['places']}>
      <Map
        onClick={onClick}
        {...options}
        style={style}
        hidden={isMapHidden}
        onLoad={handleOnLoad}
        onFullScreen={onFullScreen}
        onExitFullScreen={onExitFullScreen}
      >
        {children}
      </Map>
      {isShowStreetView && (
        <>
          <StyledImageButton id="toggle-street-view" onClick={handleIsRender} hidden={isMapHidden}>
            <img src={generateStreetViewUrl(center)} alt="street-view" />
            <StyledStreetViewSpan>Street View</StyledStreetViewSpan>
          </StyledImageButton>
          <StreetView
            style={style}
            addressControl={false}
            position={center}
            hidden={!isMapHidden}
            onStreetViewFullScreen={onStreetViewFullScreen}
          >
            <StyledBackButton id="back-to-map" onClick={handleIsRender}>
              Back to Map
            </StyledBackButton>
          </StreetView>
        </>
      )}
    </Wrapper>
  )
}

GoogleMap.propTypes = {
  render: PropTypes.func,
  style: PropTypes.objectOf(PropTypes.string.isRequired),
  callback: PropTypes.func,
  onClick: PropTypes.func,
  onZoom: PropTypes.func,
  onDragStart: PropTypes.func,
  onMapRender: PropTypes.func,
}

GoogleMap.defaultProps = {
  render: renderFunc,
  style: {},
  callback: undefined,
  onClick: undefined,
  onZoom: undefined,
  onDragStart: undefined,
  onMapRender: undefined,
}
